import React, { useState, useEffect } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { useCubeQuery } from '../../utils/hooks/useCubeQuery'
import DateSelect from '../../components/buttons/DateSelect'
import TrafficDriverModal from '../../components/modals/TrafficDriverModal'
import { useDateRange } from '../../utils/date-utils'
import { TeaserStatus, CompanyType } from '../../__generated__/graphql'
import { canSeeV2Links, useCurrentUser } from '../../utils/CurrentUserContext'
import { getV2CampaignReportUrl } from '../../utils/v2Routes'
import { GET_TRAFFIC_DRIVERS, CREATE_TRAFFIC_DRIVER, UPDATE_TRAFFIC_DRIVER } from '../../utils/api/trafficDriver'
import useCubeClient from '../../utils/cubejs-client'
import { transformTeaserDataFromCube } from './ReportNewTimePrototype/dataTransformation'
import Spinner from '../../components/icons/Spinner'
import dayjs from 'dayjs'
import { trackEvent } from '../../utils/analytics'
import { withAsyncErrorToast } from '../../utils/withToast'
import { App, Alert } from 'antd'
import { useShouldShowNewConversionReporting } from './ReportNewTimePrototype/conversionReportingGate'

// Components
import TopStats from './ReportNewTimePrototype/TopStats'
import TabSelector from './ReportNewTimePrototype/TabSelector'
import PerformanceOverview from './ReportNewTimePrototype/PerformanceOverview'
import AnalyticsDashboard from './ReportNewTimePrototype/AnalyticsDashboard'
import WebsitePerformance from './ReportNewTimePrototype/WebsitePerformance'

// Data transformation
import { 
  loadTeaserColumnPreferences, 
  saveTeaserColumnPreferences,
  loadWebsiteColumnPreferences,
  saveWebsiteColumnPreferences,
  filterValidColumns
} from './ReportNewTimePrototype/columnPreferences'
import { transformChannelDataFromCube } from './ReportNewTimePrototype/dataTransformation'

const ReportNewTimePrototype: React.FC = () => {
  const { notification } = App.useApp()
  const { user } = useCurrentUser()
  const { campaign } = useOutletContext() as { campaign: any }
  const cubejsApi = useCubeClient()
  
  // State management
  const [activeTab, setActiveTab] = useState('overview')
  const [chartType, setChartType] = useState('line')
  const [selectedMetric, setSelectedMetric] = useState('reads')
  const [selectedWebsiteMetric, setSelectedWebsiteMetric] = useState('reads')
  const [visibleColumns, setVisibleColumns] = useState<string[]>([])
  const [websiteVisibleColumns, setWebsiteVisibleColumns] = useState<string[]>([])
  const [showModal, setShowModal] = useState(false)
  const [selectedTeaser, setSelectedTeaser] = useState<any>(null)
  const [channelBreakdownType, setChannelBreakdownType] = useState<'website' | 'source'>('website')
  const [isNewConversionBannerVisible, setIsNewConversionBannerVisible] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.localStorage.getItem('hideNewConversionReportingBanner') !== 'true'
  )

  const handleCloseNewConversionBanner = () => {
    setIsNewConversionBannerVisible(false)
    window.localStorage.setItem('hideNewConversionReportingBanner', 'true')
    trackEvent('new_conversion_reporting_banner_dismissed', {
      campaignId: campaign._id,
    })
  }

  // Wrapper for channel breakdown change to track event
  const handleChannelBreakdownTypeChange = (type: 'website' | 'source') => {
    setChannelBreakdownType(type)
    trackEvent('channel_breakdown_toggled', {
      campaignId: campaign._id,
      previousType: channelBreakdownType,
      newType: type,
    })
  }

  // Track report view on mount
  useEffect(() => {
    trackEvent('report_viewed', {
      campaignId: campaign._id,
      campaignType: campaign.type,
    })
  }, [campaign._id, campaign.type])

  // Load column preferences from localStorage
  useEffect(() => {
    if (user?.company?.type) {
      const teaserCols = loadTeaserColumnPreferences(user.company.type)
      const websiteCols = loadWebsiteColumnPreferences(user.company.type)
      
      // Filter out invalid columns for the current company type
      const validTeaserCols = filterValidColumns(teaserCols, user.company.type)
      const validWebsiteCols = filterValidColumns(websiteCols, user.company.type)
      
      setVisibleColumns(validTeaserCols)
      setWebsiteVisibleColumns(validWebsiteCols)
    }
  }, [user?.company?.type])

  // Date range management - start from campaign start date like in ReportNewTime
  const tempCampStartData = dayjs(campaign.startDate)
  const campStartData = tempCampStartData.subtract(1, 'day')
  
  const {
    dateRange,
    handleDateChange,
    formattedDateRange
  } = useDateRange([
    campStartData.startOf('day'),
    dayjs().endOf('day')
  ], 300) // 300ms debounce

  const handleDate = (newValue: any) => {
    handleDateChange([
      newValue[0].startOf('day'),
      newValue[1].endOf('day'),
    ])
    trackEvent('report_date_range_changed', {
      campaignId: campaign._id,
      startDate: newValue[0].format('YYYY-MM-DD'),
      endDate: newValue[1].format('YYYY-MM-DD'),
    })
  }

  // Check if user is a publisher who doesn't own the campaign
  const isPublisherNotOwner = user.company.type === CompanyType.Publisher && campaign.ownerCompanyId !== user.companyId

  const showNewConversionReporting = useShouldShowNewConversionReporting(campaign, cubejsApi)
  const isCampaignOwner = campaign.ownerCompanyId === user.companyId

  // Get teasers for the campaign
  const { data: teasersData, loading: teasersLoading } = useQuery(GET_TRAFFIC_DRIVERS, {
    variables: {
      perPage: 1000,
      filters: {
        campaignId: campaign._id,
      }
    }
  })

  // Filter teasers client-side if publisher doesn't own campaign
  let teasers = teasersData ? teasersData.trafficDrivers.items : []
  
  // Filter by publisher's websites using the publisherId field from websites
  if (isPublisherNotOwner) {
    teasers = teasers
      .map(teaser => ({...teaser, websites: teaser.websites.filter(website => website.publisherId === user.companyId)}))
      .filter(teaser => teaser.websites.length > 0);
  }

  // Get all website IDs that belong to the publisher for cube filtering
  const publisherWebsiteIds = isPublisherNotOwner
    ? teasers.flatMap(teaser => teaser.websites.map(w => w._id))
    : null

  // Publisher filters for cube query
  const aggPublisherFilters = isPublisherNotOwner
    ? [{
        member: "aggregateTdLevel.publisherId",
        operator: "equals" as const,
        values: [user.companyId],
      }]
    : []

  // Cube query for analytics data
  const {
    resultSet: aggData,
    isLoading: aggIsLoading
  } = useCubeQuery(
    {
      dimensions: [
        "aggregateTdLevel.websiteId",
        "aggregateTdLevel.publicationId",
        "aggregateTdLevel.publisherId",
        "aggregateTdLevel.websiteName",
        "aggregateTdLevel.campaignBudget",
        "aggregateTdLevel.trafficDriverId",
        "aggregateTdLevel.tdHeadline",
        "aggregateTdLevel.tdImage",
        "aggregateTdLevel.campaignType",
        "aggregateTdLevel.tdType",
        "aggregateTdLevel.utmSource",
        "aggregateTdLevel.utmMedium",
      ],
      measures: [
        "aggregateTdLevel.tdImpressions",
        "aggregateTdLevel.tdClicks",
        "aggregateTdLevel.reads",
        "aggregateTdLevel.totalCost",
        "aggregateTdLevel.publisherRevenue",
        "aggregateTdLevel.total_readtime",
        "aggregateTdLevel.clickouts",
        "aggregateTdLevel.conversions",
      ],
      order: {
        "aggregateTdLevel.websiteId": "asc",
      },
      filters: [
        {
          member: "aggregateTdLevel.campaignId",
          operator: "equals" as const,
          values: [campaign._id],
        },
        {
          member: "aggregateTdLevel.websiteId",
          operator: "set" as const,
        },
        {
          member: "aggregateTdLevel.publicationId",
          operator: "set" as const,
        },
        {
          member: "aggregateTdLevel.fraud",
          operator: "notEquals" as const,
          values: ["true"],
        },
        ...aggPublisherFilters,
      ],
      timeDimensions: [
        {
          dimension: "aggregateTdLevel.date",
          dateRange: formattedDateRange,
        },
      ],
    },
    { cubejsApi: cubejsApi as any }
  )

  // Process the data using the new transformation function
  // Pass publisher website filter for non-owner publishers
  const publisherWebsiteFilter = isPublisherNotOwner ? publisherWebsiteIds : null
  
  const result = aggData ? transformTeaserDataFromCube(
    teasers, 
    (aggData as any).loadResponses[0].data, 
    user.company.type,
    publisherWebsiteFilter
  ) : { teaserData: [], totals: {} }
  const { teaserData, totals } = result

  // Debug logging for null values
  console.log('Debug: teaserData structure:', teaserData)
  teaserData.forEach((teaser, index) => {
    if (!teaser.children) {
      console.error(`Debug: Teaser at index ${index} has null/undefined children:`, teaser)
    } else if (!Array.isArray(teaser.children)) {
      console.error(`Debug: Teaser at index ${index} has non-array children:`, teaser.children)
    }
  })

  // Transform channel data from real cube data
  const analyticsData = aggData ? {
    channelData: transformChannelDataFromCube((aggData as any).loadResponses[0].data, publisherWebsiteFilter, channelBreakdownType)
  } : { channelData: [] }
  
  // Calculate totals from all channels (not just teasers)
  const allChannelsTotals = analyticsData.channelData.reduce((acc, channel) => {
    acc.reads += channel.reads || 0
    acc.clickOuts += channel.clickOuts || 0
    acc.conversions += channel.conversions || 0
    return acc
  }, { reads: 0, clickOuts: 0, conversions: 0 })
  
  // Calculate total readtime from all raw data (both teasers and other channels)
  const allDataTotalReadtime = aggData ? (aggData as any).loadResponses[0].data.reduce((sum: number, item: any) => {
    return sum + (parseFloat(item['aggregateTdLevel.total_readtime']) || 0)
  }, 0) : 0
  
  // Create combined totals object
  const combinedTotals = {
    ...totals, // This includes teaser-specific metrics like tdImpressions, tdClicks, etc.
    // Override with combined metrics for reads, clickOuts, and avgReadtime
    reads: allChannelsTotals.reads,
    clickOuts: allChannelsTotals.clickOuts,
    conversions: allChannelsTotals.conversions,
    totalReadtime: allDataTotalReadtime,
    avgReadtime: allChannelsTotals.reads > 0 ? allDataTotalReadtime / allChannelsTotals.reads / 1000 : 0
  }
  
  // Create website data by grouping teasers by website
  // Filter by publisher websites if user is not campaign owner
  const websiteMap = new Map()
  teaserData.forEach(teaser => {
    // Check if teaser.children is null and log
    if (!teaser.children) {
      console.error('Found teaser with null/undefined children:', teaser)
      return
    }
    teaser.children.forEach(child => {
      const websiteId = child.websiteId
      
      // Skip if publisher doesn't own this website
      if (isPublisherNotOwner && publisherWebsiteIds && !publisherWebsiteIds.includes(websiteId)) {
        return
      }
      
      if (!websiteMap.has(websiteId)) {
        websiteMap.set(websiteId, {
          key: websiteId,
          websiteId: websiteId,
          name: child.name || 'Unknown Website',
          websiteName: child.name || 'Unknown Website',
          tdImpressions: 0,
          tdClicks: 0,
          reads: 0,
          publisherRevenue: 0,
          totalCost: 0,
          clickOuts: 0,
          conversions: 0,
          totalReadtime: 0,
          avgReadtime: 0,
          children: []
        })
      }
      
      const website = websiteMap.get(websiteId)
      website.children.push({
        key: `${teaser.trafficDriverId}_${websiteId}`,
        ...child,
        name: teaser.name  // Teaser name should override child.name (website name)
      })
      
      // Aggregate website totals
      website.tdImpressions += child.tdImpressions || 0
      website.tdClicks += child.tdClicks || 0
      website.reads += child.reads || 0
      website.publisherRevenue += child.publisherRevenue || 0
      website.totalCost += child.totalCost || 0
      website.clickOuts += child.clickOuts || 0
      website.conversions += child.conversions || 0
      website.totalReadtime += child.totalReadtime || 0
    })
  })

  const websiteData = Array.from(websiteMap.values())
    .filter(website => {
      // Filter out invalid websites (those with "None" as ID or null name)
      return website.websiteId && website.websiteId !== "None" && website.name !== null
    })
    .map(website => {
      // Calculate derived metrics for each website
      website.ctr = website.tdImpressions > 0 ? ((website.tdClicks / website.tdImpressions) * 100).toFixed(2) : 0
      // Calculate eCPM based on user type: publisherRevenue for publishers, totalCost for advertisers/agencies
      const revenueForEcpm = user.company.type === CompanyType.Publisher ? website.publisherRevenue : website.totalCost
      website.eCpm = website.tdImpressions > 0 ? (revenueForEcpm / (website.tdImpressions / 1000)).toFixed(2) : 0
      website.cpa = website.conversions > 0 ? (website.totalCost / website.conversions).toFixed(2) : 0
      website.avgReadtime = website.reads > 0 ? Math.round((website.totalReadtime / 1000) / website.reads * 10) / 10 : 0
      website.imps_reads = website.tdImpressions > 0 ? ((website.reads / website.tdImpressions) * 1000).toFixed(2) : 0
      website.cotr = website.reads > 0 ? ((website.clickOuts / website.reads) * 100).toFixed(2) : 0
      return website
    })
    .sort((a, b) => b.reads - a.reads)

  // Calculate website-specific totals
  const websiteTotals = websiteData.reduce((acc, website) => {
    acc.tdImpressions += website.tdImpressions
    acc.tdClicks += website.tdClicks
    acc.reads += website.reads
    acc.publisherRevenue += website.publisherRevenue
    acc.totalCost += website.totalCost
    acc.clickOuts += website.clickOuts
    acc.conversions += website.conversions
    // Calculate totalReadtime from website children
    website.children.forEach(child => {
      acc.totalReadtime += child.totalReadtime || 0
    })
    return acc
  }, { 
    tdImpressions: 0, 
    tdClicks: 0, 
    reads: 0, 
    publisherRevenue: 0, 
    totalCost: 0, 
    clickOuts: 0, 
    conversions: 0,
    totalReadtime: 0
  }) as any

  // Calculate derived metrics for website totals
  websiteTotals.ctr = websiteTotals.tdImpressions > 0 ? (websiteTotals.tdClicks / websiteTotals.tdImpressions) * 100 : 0
  // Calculate eCPM based on user type: publisherRevenue for publishers, totalCost for advertisers/agencies
  const totalRevenueForEcpm = user.company.type === CompanyType.Publisher ? websiteTotals.publisherRevenue : websiteTotals.totalCost
  websiteTotals.eCpm = websiteTotals.tdImpressions > 0 ? totalRevenueForEcpm / (websiteTotals.tdImpressions / 1000) : 0
  websiteTotals.cpa = websiteTotals.conversions > 0 ? websiteTotals.totalCost / websiteTotals.conversions : 0
  websiteTotals.avgReadtime = websiteTotals.reads > 0 ? websiteTotals.totalReadtime / websiteTotals.reads : 0
  websiteTotals.imps_reads = websiteTotals.tdImpressions > 0 ? (websiteTotals.reads / websiteTotals.tdImpressions) * 1000 : 0
  websiteTotals.cotr = websiteTotals.reads > 0 ? (websiteTotals.clickOuts / websiteTotals.reads) * 100 : 0

  // Mutations for teaser management
  const [createTrafficDriver] = useMutation(CREATE_TRAFFIC_DRIVER, {
    refetchQueries: ["TeaserMany"],
    awaitRefetchQueries: true,
  })
  const [updateTrafficDriver] = useMutation(UPDATE_TRAFFIC_DRIVER, {
    refetchQueries: ["TeaserMany"],
    awaitRefetchQueries: true,
  })

  // Column change handlers with localStorage persistence
  const handleVisibleColumnsChange = (newColumns: string[]) => {
    if (user?.company?.type) {
      // Filter out invalid columns for current company type before saving
      const validColumns = filterValidColumns(newColumns, user.company.type)
      setVisibleColumns(validColumns)
      saveTeaserColumnPreferences(validColumns, user.company.type)
      trackEvent('report_columns_customized', {
        campaignId: campaign._id,
        tableType: 'teaser',
        columnCount: validColumns.length,
      })
    } else {
      setVisibleColumns(newColumns)
    }
  }

  const handleWebsiteVisibleColumnsChange = (newColumns: string[]) => {
    if (user?.company?.type) {
      // Filter out invalid columns for current company type before saving
      const validColumns = filterValidColumns(newColumns, user.company.type)
      setWebsiteVisibleColumns(validColumns)
      saveWebsiteColumnPreferences(validColumns, user.company.type)
      trackEvent('report_columns_customized', {
        campaignId: campaign._id,
        tableType: 'website',
        columnCount: validColumns.length,
      })
    } else {
      setWebsiteVisibleColumns(newColumns)
    }
  }

  // Loading state
  if (aggIsLoading || teasersLoading) {
    return <Spinner />
  }

  // Action handlers for teaser management
  const handleCreateTrafficDriver = async (record: any) => {
    const key = selectedTeaser ? 'Update teaser' : 'Create teaser'
    try {
      await withAsyncErrorToast(key, async () => {
        if (selectedTeaser) {
          await updateTrafficDriver({
            variables: {
              id: selectedTeaser.trafficDriverId,
              record
            }
          })
          trackEvent('teaser_updated', {
            campaignId: campaign._id,
            teaserId: selectedTeaser.trafficDriverId,
            websiteCount: record.websiteIds?.length,
          })
        } else {
          const res = await createTrafficDriver({ variables: { record } })
          trackEvent('teaser_created', {
            campaignId: campaign._id,
            teaserId: res.data?.trafficDriverCreateOne?._id,
            websiteCount: record.websiteIds?.length,
          })
        }
      }, undefined, notification)
      setShowModal(false)
      setSelectedTeaser(null)
    } catch (error) {
      trackEvent('error_occurred', {
        errorType: 'teaser_save_error',
        campaignId: campaign._id,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }

  const onUpdateStatus = async (row: any, status: TeaserStatus) => {
    try {
      await withAsyncErrorToast('Update teaser status', async () => {
        await updateTrafficDriver({
          variables: {
            id: row.trafficDriverId,
            record: {
              status,
              websiteIds: row.children?.map((child: any) => child.websiteId) || []
            }
          }
        })
        trackEvent('teaser_status_changed', {
          campaignId: campaign._id,
          teaserId: row.trafficDriverId,
          newStatus: status,
        })
      }, undefined, notification)
    } catch (error) {
      trackEvent('error_occurred', {
        errorType: 'teaser_status_update_error',
        campaignId: campaign._id,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }

  const onCreateOrUpdate = (row?: any) => {
    setSelectedTeaser(row || null)
    setShowModal(true)
    trackEvent('modal_opened', {
      modalName: 'traffic_driver',
      campaignId: campaign._id,
      editMode: !!row,
    })
  }

  // Render content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <PerformanceOverview
            visibleColumns={visibleColumns}
            onVisibleColumnsChange={handleVisibleColumnsChange}
            onCreateOrUpdate={onCreateOrUpdate}
            onUpdateStatus={onUpdateStatus}
            selectedMetric={selectedMetric}
            companyType={user.company.type}
            teaserData={teaserData}
            channelData={analyticsData.channelData}
            channelBreakdownType={channelBreakdownType}
            onChannelBreakdownTypeChange={setChannelBreakdownType}
            cubejsApi={cubejsApi}
            campaignId={campaign._id}
            formattedDateRange={formattedDateRange}
            aggPublisherFilters={aggPublisherFilters}
            teasers={teasers}
            hideConversionColumns={showNewConversionReporting}
          />
        )
      case 'analytics':
        return (
          <AnalyticsDashboard
            chartType={chartType}
            onChartTypeChange={setChartType}
            selectedMetric={selectedMetric}
            onSelectedMetricChange={setSelectedMetric}
            companyType={user.company.type}
            dateRange={formattedDateRange}
            cubejsApi={cubejsApi}
            campaignId={campaign._id}
            aggPublisherFilters={aggPublisherFilters}
            channelData={analyticsData.channelData}
            teaserData={teaserData}
          />
        )
      case 'websites':
        return (
          <WebsitePerformance
            websiteVisibleColumns={websiteVisibleColumns}
            onWebsiteVisibleColumnsChange={handleWebsiteVisibleColumnsChange}
            selectedWebsiteMetric={selectedWebsiteMetric}
            onSelectedWebsiteMetricChange={setSelectedWebsiteMetric}
            companyType={user.company.type}
            websiteData={websiteData}
            cubejsApi={cubejsApi}
            campaignId={campaign._id}
            formattedDateRange={formattedDateRange}
            aggPublisherFilters={aggPublisherFilters}
            hideConversionColumns={showNewConversionReporting}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className="mb-4">
      {/* New Conversion Reporting Banner */}
      {showNewConversionReporting && isCampaignOwner && isNewConversionBannerVisible && canSeeV2Links(user) && (
        <Alert
          className="mb-4"
          type="info"
          showIcon
          closable
          onClose={handleCloseNewConversionBanner}
          message="Goodbye conversions, welcome outcomes"
          description={
            <span>
              We're migrating from one-size-fits-all Conversions to named campaign outcomes — Leads, Purchases, and beyond. See the new report in V2.{' '}
              <a
                href={getV2CampaignReportUrl(user.companyId, campaign._id)}
                target="_blank"
                rel="noreferrer"
                className="font-semibold underline"
                onClick={() => trackEvent('new_conversion_reporting_opened', {
                  campaignId: campaign._id,
                  source: 'report_banner',
                })}
              >
                Open V2 Report
              </a>
              {/* {' · '}
              <span className="text-gray-500">Read migration documentation</span> */}
            </span>
          }
        />
      )}

      {/* Top Stats */}
      <TopStats totalData={combinedTotals} />

      {/* Controls Row */}
      <div className="flex mt-4 justify-between w-full items-center mb-4">
        <DateSelect dateRange={dateRange} handleDate={handleDate} />
        <TabSelector activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      {/* Tab Content */}
      {renderTabContent()}

      {/* Teaser Management Modal */}
      <TrafficDriverModal
        open={showModal}
        editMode={!!selectedTeaser}
        defaultValues={selectedTeaser ? {
          headline: selectedTeaser.name,
          websiteIds: selectedTeaser.children?.map((child: any) => child.websiteId) || [],
          publicationId: selectedTeaser.publicationId,
          imageId: selectedTeaser.tdImage,
        } : {
          campaignId: campaign._id,
        }}
        websites={campaign.websites}
        onCancel={() => {
          setSelectedTeaser(null)
          setShowModal(false)
        }}
        onOk={handleCreateTrafficDriver}
      />
    </div>
  )
}

export default ReportNewTimePrototype
